import { useCallback, useRef, useState } from 'react';

export type HistoryState<T> = {
  past: T[];
  present: T;
  future: T[];
};

export type UseAnnotationHistoryReturn<T> = {
  state: T;
  setState: (newState: T) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  clearHistory: () => void;
};

/**
 * Hook for managing undo/redo history for annotation states
 * Based on the Redux undo pattern but simplified for local component state
 */
export function useAnnotationHistory<T>(initialState: T, maxHistorySize = 50): UseAnnotationHistoryReturn<T> {
  const [history, setHistory] = useState<HistoryState<T>>({
    past: [],
    present: initialState,
    future: [],
  });

  // Track if we're in the middle of an undo/redo operation to prevent history updates
  const isUndoRedoRef = useRef(false);

  const setState = useCallback(
    (newState: T) => {
      // Don't add to history if we're in an undo/redo operation
      if (isUndoRedoRef.current) {
        setHistory((prev) => ({
          ...prev,
          present: newState,
        }));
        return;
      }

      setHistory((prev) => {
        const newPast = [...prev.past, prev.present];

        // Limit history size to prevent memory issues
        if (newPast.length > maxHistorySize) {
          newPast.shift();
        }

        return {
          past: newPast,
          present: newState,
          future: [], // Clear future when new state is set
        };
      });
    },
    [maxHistorySize],
  );

  const undo = useCallback(() => {
    setHistory((prev) => {
      if (prev.past.length === 0) return prev;

      isUndoRedoRef.current = true;

      const previous = prev.past[prev.past.length - 1];
      const newPast = prev.past.slice(0, prev.past.length - 1);

      // Reset the flag after state update
      setTimeout(() => {
        isUndoRedoRef.current = false;
      }, 0);

      return {
        past: newPast,
        present: previous,
        future: [prev.present, ...prev.future],
      };
    });
  }, []);

  const redo = useCallback(() => {
    setHistory((prev) => {
      if (prev.future.length === 0) return prev;

      isUndoRedoRef.current = true;

      const next = prev.future[0];
      const newFuture = prev.future.slice(1);

      // Reset the flag after state update
      setTimeout(() => {
        isUndoRedoRef.current = false;
      }, 0);

      return {
        past: [...prev.past, prev.present],
        present: next,
        future: newFuture,
      };
    });
  }, []);

  const clearHistory = useCallback(() => {
    setHistory((prev) => ({
      past: [],
      present: prev.present,
      future: [],
    }));
  }, []);

  const canUndo = history.past.length > 0;
  const canRedo = history.future.length > 0;

  return {
    state: history.present,
    setState,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory,
  };
}
